/**
 * Feed-Videos (Marcel 13.10.2026) – REINE Regeln für Beitrags-Medien, EINE Stelle (Web).
 * Backend liefert `media[]` (Bild/Video mit Poster, Dauer, Maßen, Status); alte Beiträge haben nur `images`.
 */
export type PostMediaType = 'image' | 'video';
export type PostVideoStatus = 'processing' | 'ready' | 'failed';

export interface PostMedia {
  type: PostMediaType;
  /** Bilddatei bzw. 'videos/<id>.mp4' (über /api/file, Range-fähig) */
  file: string;
  poster?: string | null;
  duration?: number | null;
  width?: number | null;
  height?: number | null;
  status?: PostVideoStatus | null;
  videoId?: string | null;
}

export const VIDEO_MAX_SECONDS = 180;
export const VIDEO_MAX_BYTES = 300 * 1024 * 1024;
export const VIDEO_PLAY_THRESHOLD = 0.6;
export const VIDEO_MILESTONES = [25, 50, 75, 100] as const;

/** `media` aus der API lesen; fehlt es (alte Beiträge), aus `images` ableiten. Ungültiges wird verworfen. */
export function parsePostMedia(data: any): PostMedia[] {
  const raw = Array.isArray(data?.media) ? data.media : null;
  if (raw && raw.length > 0) {
    const out: PostMedia[] = [];
    for (const m of raw) {
      if (!m || typeof m !== 'object' || typeof m.file !== 'string' || !m.file.trim()) continue;
      const type: PostMediaType = m.type === 'video' ? 'video' : 'image';
      const status = m.status === 'ready' || m.status === 'failed' ? m.status : (type === 'video' ? 'processing' : null);
      out.push({
        type,
        file: m.file,
        poster: typeof m.poster === 'string' && m.poster.trim() ? m.poster : null,
        duration: typeof m.duration === 'number' && m.duration >= 0 ? m.duration : null,
        width: typeof m.width === 'number' && m.width > 0 ? m.width : null,
        height: typeof m.height === 'number' && m.height > 0 ? m.height : null,
        status,
        videoId: typeof m.videoId === 'string' && m.videoId ? m.videoId : null,
      });
    }
    if (out.length > 0) return out;
  }
  const images = Array.isArray(data?.images) ? data.images.filter((i: any) => typeof i === 'string' && i.trim().length > 0) : [];
  return images.map((file: string) => ({ type: 'image', file, poster: null, duration: null, width: null, height: null, status: null, videoId: null }));
}

/** Video-Datei (Browser-File) nach Typ/Endung erkennen. */
export function isVideoFile(file: { type?: string; name?: string } | null | undefined): boolean {
  if (!file) return false;
  if (typeof file.type === 'string' && file.type.startsWith('video/')) return true;
  const name = typeof file.name === 'string' ? file.name.toLowerCase() : '';
  return /\.(mp4|mov|m4v|webm|mkv|3gp)$/.test(name);
}

/** Vorprüfung ohne Netz: Größe + Dauer (Dauer aus den Metadaten des Browsers, null = unbekannt → Backend prüft). */
export function validateVideoSelection({ size, duration }: { size: number; duration: number | null }): { valid: boolean; error?: string } {
  if (!(size > 0)) return { valid: false, error: 'empty' };
  if (size > VIDEO_MAX_BYTES) return { valid: false, error: 'too-large' };
  if (duration != null && Number.isFinite(duration) && duration > VIDEO_MAX_SECONDS + 0.5) return { valid: false, error: 'too-long' };
  return { valid: true };
}

/** Neue Meilensteine (25/50/75/100) für einen Fortschritt in Prozent, die noch nicht gemeldet wurden. */
export function newMilestones(progressPercent: number, reported: ReadonlySet<number>): number[] {
  if (!Number.isFinite(progressPercent)) return [];
  return VIDEO_MILESTONES.filter((m) => progressPercent >= m && !reported.has(m));
}

/** „0:07“ / „2:35“ */
export function formatDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Anteil sichtbar (0..1) → spielen? */
export function shouldAutoplay(intersectionRatio: number, threshold = VIDEO_PLAY_THRESHOLD): boolean {
  return Number.isFinite(intersectionRatio) && intersectionRatio >= threshold;
}

/** Medien eines Beitrags (auch für Fixtures/alte Objekte ohne `media`). */
export function mediaOf(post: { media?: PostMedia[] | null; images?: string[] | null } | null | undefined): PostMedia[] {
  if (!post) return [];
  if (Array.isArray(post.media) && post.media.length > 0) return post.media;
  return parsePostMedia({ images: post.images ?? [] });
}
